import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChatTheme, Question } from "@/types/theme";
import { UnifiedChatWidget } from "@/components/chat/UnifiedChatWidget";
import { useState, useEffect, useCallback, useRef } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { useParams } from "react-router-dom";
import { useChatbot } from "@/hooks/useChatbot";
import { OperatorSettings } from "./OperatorSettings";
import { CompanyInfoSettings } from "./CompanyInfoSettings";
import { useToast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { X } from "lucide-react";
import { ImageIcon } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { prepareThemeForDatabase } from "@/utils/themeUtils";

interface BuilderStyleProps {
  theme: ChatTheme;
  onUpdateTheme: (theme: Partial<ChatTheme>) => void;
  reportUnsavedChanges?: (isDirty: boolean) => void;
}

interface CompanyInfoFormValues {
  companyName: string;
  companyLogo?: string | null;
}

// Default theme values for reset functionality
const defaultTheme: ChatTheme = {
  id: 'default',
  chatbot_id: 'default',
  name: 'Default Theme',
  primarycolor: '#2563EB',
  backgroundcolor: '#FFFFFF',
  userbubblecolor: '#2563EB',
  botbubblecolor: '#F3F4F6',
  usertextcolor: '#FFFFFF',
  bottextcolor: '#1F2937',
  headercolor: '#2563EB',
  headertextcolor: '#FFFFFF',
  borderradius: '0.75rem',
  fontfamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  buttoncolor: '#2563EB',
  buttontextcolor: '#FFFFFF',
  position: 'right',
  buttontext: 'Chat with us',
  welcomebubblecolor: '#EFF6FF',
  welcomebubbletextcolor: '#1E40AF',
  inputbordercolor: '#E5E7EB',
  inputborderradius: '0.375rem',
  inputbackgroundcolor: '#FFFFFF',
  avatarbackgroundcolor: 'rgba(0,0,0,0.1)',
  headerbackgroundcolor: '#4FD1C5',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  showavatar: true,
  showtitle: true,
  showclosebutton: true,
  showreset: true,
  showsources: true,
  showtimestamp: true,
  showtypingindicator: true,
  showuseravatar: false
};

export const BuilderStyle = ({ theme, onUpdateTheme, reportUnsavedChanges }: BuilderStyleProps) => {
  const [localTheme, setLocalTheme] = useState<ChatTheme>(theme);
  const { id: chatbotId } = useParams<{ id: string }>();
  const [previewTitle, setPreviewTitle] = useState("OfferChat Support");
  const {
    chatbot,
    questions: transformedQuestions,
    triggerRules: transformedTriggerRules,
    save: updateChatbot,
  } = useChatbot(chatbotId);
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [removeLogoOnSave, setRemoveLogoOnSave] = useState(false);

  // Delay settings state
  const [messageDelay, setMessageDelay] = useState(1000);
  const [buttonOptionsDelay, setButtonOptionsDelay] = useState(0);
  const [noResponseDelay, setNoResponseDelay] = useState(2000);
  const [optionsHandlerDelay, setOptionsHandlerDelay] = useState(10);

  // Debounce refs for delay settings
  const messageDelayTimeoutRef = useRef<NodeJS.Timeout>();
  const buttonOptionsDelayTimeoutRef = useRef<NodeJS.Timeout>();
  const noResponseDelayTimeoutRef = useRef<NodeJS.Timeout>();
  const optionsHandlerDelayTimeoutRef = useRef<NodeJS.Timeout>();

  const companyInfoForm = useForm<CompanyInfoFormValues>({
    defaultValues: {
      companyName: chatbot?.company_name || '',
      companyLogo: chatbot?.company_logo || null,
    }
  });

  useEffect(() => {
    setLocalTheme(theme);
    if (chatbot) {
      companyInfoForm.reset({
        companyName: chatbot.company_name || '',
        companyLogo: chatbot.company_logo || null,
      });
      setCompanyLogoUrl(chatbot.company_logo || null);
      
      // Load delay settings from chatbot settings
      const settings = chatbot.settings as any || {};
      setMessageDelay(settings.messageDelay ?? 1000);
      setButtonOptionsDelay(settings.buttonOptionsDelay ?? 0);
      setNoResponseDelay(settings.noResponseDelay ?? 2000);
      setOptionsHandlerDelay(settings.optionsHandlerDelay ?? 10);
    }
  }, [theme, chatbot, companyInfoForm]);

  useEffect(() => {
    if (chatbot?.title) {
      setPreviewTitle(chatbot.title);
    }
  }, [chatbot]);

  const handleColorChange = (key: keyof ChatTheme, value: string) => {
    const updatedTheme = {
      ...localTheme,
      [key]: value,
    };
    setLocalTheme(updatedTheme);
    onUpdateTheme(updatedTheme);
  };

  const handlePrimaryColorChange = (value: string) => {
    handleColorChange('primarycolor', value);
  };

  const handleBackgroundColorChange = (value: string) => {
    handleColorChange('backgroundcolor', value);
  };

  const handleUserBubbleColorChange = (value: string) => {
    handleColorChange('userbubblecolor', value);
  };

  const handleBotBubbleColorChange = (value: string) => {
    handleColorChange('botbubblecolor', value);
  };

  const handleUserTextColorChange = (value: string) => {
    handleColorChange('usertextcolor', value);
  };

  const handleBotTextColorChange = (value: string) => {
    handleColorChange('bottextcolor', value);
  };

  const handleHeaderColorChange = (value: string) => {
    handleColorChange('headercolor', value);
  };

  const handleHeaderTextColorChange = (value: string) => {
    handleColorChange('headertextcolor', value);
  };

  const handleBorderRadiusChange = (value: string) => {
    handleColorChange('borderradius', value);
  };

  const handleFontFamilyChange = (value: string) => {
    handleColorChange('fontfamily', value);
  };

  const handleTitleChange = (newTitle: string) => {
    setPreviewTitle(newTitle);
  };

  const handleResetToDefaults = () => {
    const resetTheme = { ...defaultTheme, id: localTheme.id, chatbot_id: localTheme.chatbot_id };
    setLocalTheme(resetTheme);
    onUpdateTheme(resetTheme);
    if (chatbot) {
      setPreviewTitle(chatbot.title || "OfferChat Support");
    }
  };

  const onCompanyInfoSubmit = (data: CompanyInfoFormValues) => {
    // Update local state only, don't save yet
    // Company info will be saved via main Save Changes button
    // Logo upload/removal will be handled during save
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
  };

  const handleCompanyLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (!['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'].includes(file.type)) {
        toast({ variant: "destructive", title: "Invalid file type", description: "Please upload a JPG, PNG, GIF, or SVG." });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast({ variant: "destructive", title: "File too large", description: "Logo image must be less than 2MB." });
        return;
      }

      setPendingLogoFile(file);
      setRemoveLogoOnSave(false);

      const reader = new FileReader();
      reader.onload = (e) => {
        setCompanyLogoUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCompanyLogo = () => {
    setPendingLogoFile(null);
    setRemoveLogoOnSave(true);
    companyInfoForm.setValue('companyLogo', null);
    toast({
        title: "Logo marked for removal",
        description: "Save changes to remove the company logo.",
      });
  };

  const handleMessageDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setMessageDelay(delayValue);
    
    // Update local settings only, don't save yet
    if (chatbotId && chatbot) {
      const currentSettings = (chatbot.settings as any) || {};
      // Update through onUpdateTheme if it's part of theme, or through settings
      // For now, just mark as dirty
      if (reportUnsavedChanges) {
        reportUnsavedChanges(true);
      }
    }
  }, [chatbotId, chatbot, reportUnsavedChanges]);

  const handleButtonOptionsDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setButtonOptionsDelay(delayValue);
    
    // Update local state only, don't save yet
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
  }, [reportUnsavedChanges]);

  const handleNoResponseDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setNoResponseDelay(delayValue);
    
    // Update local state only, don't save yet
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
  }, [reportUnsavedChanges]);

  const handleOptionsHandlerDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setOptionsHandlerDelay(delayValue);
    
    // Update local state only, don't save yet
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
  }, [reportUnsavedChanges]);

  const handleTimestampToggle = (checked: boolean) => {
    const updatedTheme = {
      ...localTheme,
      showtimestamp: checked,
    };
    setLocalTheme(updatedTheme);
    
    // Update theme (showtimestamp is in theme, will be saved via main Save Changes button)
    onUpdateTheme({ showtimestamp: checked });
    
    // Mark as dirty
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (messageDelayTimeoutRef.current) clearTimeout(messageDelayTimeoutRef.current);
      if (buttonOptionsDelayTimeoutRef.current) clearTimeout(buttonOptionsDelayTimeoutRef.current);
      if (noResponseDelayTimeoutRef.current) clearTimeout(noResponseDelayTimeoutRef.current);
      if (optionsHandlerDelayTimeoutRef.current) clearTimeout(optionsHandlerDelayTimeoutRef.current);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Style Settings</h3>
        </div>

        <Tabs defaultValue="colors" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="colors">Colors & Appearance</TabsTrigger>
            <TabsTrigger value="operator">Operator Settings</TabsTrigger>
            <TabsTrigger value="company">Company Info</TabsTrigger>
          </TabsList>

          <TabsContent value="colors" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Color & Appearance Settings</CardTitle>
                <CardDescription>Customize the visual appearance of your chat widget</CardDescription>
                <Button variant="outline" size="sm" onClick={handleResetToDefaults}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset to Defaults
              </Button>
              </CardHeader>
              <CardContent className="space-y-4">
             
                {/* Color inputs in a 3-column grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.primarycolor}
                        onChange={(e) => handlePrimaryColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.primarycolor}
                        onChange={(e) => handlePrimaryColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.backgroundcolor}
                        onChange={(e) => handleBackgroundColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.backgroundcolor}
                        onChange={(e) => handleBackgroundColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>User Bubble Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.userbubblecolor}
                        onChange={(e) => handleUserBubbleColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.userbubblecolor}
                        onChange={(e) => handleUserBubbleColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bot Bubble Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.botbubblecolor}
                        onChange={(e) => handleBotBubbleColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.botbubblecolor}
                        onChange={(e) => handleBotBubbleColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>User Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.usertextcolor}
                        onChange={(e) => handleUserTextColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.usertextcolor}
                        onChange={(e) => handleUserTextColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bot Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.bottextcolor}
                        onChange={(e) => handleBotTextColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.bottextcolor}
                        onChange={(e) => handleBotTextColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Header Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.headercolor}
                        onChange={(e) => handleHeaderColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.headercolor}
                        onChange={(e) => handleHeaderColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Header Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.headertextcolor}
                        onChange={(e) => handleHeaderTextColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.headertextcolor}
                        onChange={(e) => handleHeaderTextColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Separate section for non-color inputs */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>Border Radius</Label>
                    <Input
                      type="text"
                      value={localTheme.borderradius}
                      onChange={(e) => handleBorderRadiusChange(e.target.value)}
                      placeholder="1rem"
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Font Family</Label>
                    <Select value={localTheme.fontfamily} onValueChange={handleFontFamilyChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select font family" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-apple-system, BlinkMacSystemFont, 'SF Pro', 'San Francisco', 'Helvetica Neue', Arial, sans-serif">SF Pro (iMessage)</SelectItem>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Arial">Arial</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Helvetica">Helvetica</SelectItem>
                        <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Verdana">Verdana</SelectItem>
                        <SelectItem value="system-ui">System UI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Show Timestamp: </Label><br></br>
                    <Switch checked={localTheme.showtimestamp} onCheckedChange={handleTimestampToggle} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="operator" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Operator Settings</CardTitle>
                <CardDescription>Configure the operator display in your chat widget</CardDescription>
              </CardHeader>
              <CardContent>
                {chatbotId && chatbot && (
                  <OperatorSettings
                    chatbotId={chatbotId}
                    operatorName={chatbot.operator_name}
                    operatorAvatarUrl={chatbot.operator_avatar_url}
                    reportUnsavedChanges={reportUnsavedChanges}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="company" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>Update your company details displayed in the chat widget</CardDescription>
              </CardHeader>
              <CardContent>
                {chatbotId && chatbot && (
                    <CompanyInfoSettings
                        chatbotId={chatbotId}
                        companyName={chatbot.company_name}
                        companyLogo={chatbot.company_logo}
                        reportUnsavedChanges={reportUnsavedChanges}
                    />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-4">Live Preview</h3>
        <div className="border rounded-lg p-4 bg-gray-50 shadow-sm relative overflow-hidden" style={{ height: '600px' }}>
          <iframe
            key={`${messageDelay}-${buttonOptionsDelay}-${noResponseDelay}-${optionsHandlerDelay}-${localTheme.showtimestamp}-${chatbot?.company_name || ''}-${companyLogoUrl || ''}-${chatbot?.operator_avatar_url || ''}`}
            src={`/chat/${chatbotId}?theme=${encodeURIComponent(JSON.stringify(localTheme))}`}
            title="Live Chat Preview"
            className="w-full h-full border-none"
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">
          This preview shows the <strong>actual production chat widget</strong> - exactly how users will experience it.
        </p>
      </div>
    </div>
  );
};
